/**
 * Fisica predictiva y utilidad de pases para la IA.
 * Proyecta trayectorias con friccion del cesped e intercepciones rivales.
 */
import {
  AI,
  BALL,
  FIELD,
  KICK,
  isInOffsidePosition,
  modeKick,
  modeSpeed,
  offsideLineX,
  PLAYER,
  fieldPlayersPerTeam,
  playerRoleProfile,
  type Ball,
  type Player,
  type Vec2,
} from "@golazo/shared";
import { clamp, len2 } from "../core/math.js";
// Determinista entre motores: ver `core/detMath.ts`.
import { dlog } from "../core/detMath.js";
import { aimScale } from "./skill.js";

export interface RoleSkills {
  vision: number;
  passing: number;
  anticipation: number;
  placement: number;
  pace: number;
}

export function roleSkills(player: Player): RoleSkills {
  const profile = playerRoleProfile(player);
  return {
    vision: profile.vision,
    passing: profile.passing,
    anticipation: profile.anticipation,
    placement: profile.placement,
    pace: player.speedMultiplier,
  };
}

function dist(a: Vec2, b: Vec2): number {
  return len2(a.x - b.x, a.y - b.y);
}

function distanceToSegment(p: Vec2, a: Vec2, b: Vec2): number {
  const abx = b.x - a.x;
  const aby = b.y - a.y;
  const lenSq = abx * abx + aby * aby;
  if (lenSq < 1e-6) return dist(p, a);

  const t = clamp(((p.x - a.x) * abx + (p.y - a.y) * aby) / lenSq, 0, 1);
  return len2(p.x - (a.x + abx * t), p.y - (a.y + aby * t));
}

function closestPointOnSegment(p: Vec2, a: Vec2, b: Vec2): Vec2 {
  const abx = b.x - a.x;
  const aby = b.y - a.y;
  const lenSq = abx * abx + aby * aby;
  if (lenSq < 1e-6) return { x: a.x, y: a.y };
  const t = clamp(((p.x - a.x) * abx + (p.y - a.y) * aby) / lenSq, 0, 1);
  return { x: a.x + abx * t, y: a.y + aby * t };
}

/**
 * Tiempo de vuelo con rozamiento exponencial del cesped.
 *
 * El caso de "no llega" devolvia `maxReach * 0.85`, que es una DISTANCIA usada
 * como segundos: con un pase normal salian 787 "segundos" de vuelo. No era
 * academico. `passInterceptRisk` divide por este numero para sacar el margen
 * del defensa, y con 787 en el denominador el margen daba ~1 y el riesgo 0:
 * **todo pase largo se evaluaba como perfectamente seguro**. De ahi salia buena
 * parte del gusto de la IA por el balonazo.
 *
 * El tope correcto es el tiempo que tarda en recorrer ese 97% del alcance, que
 * con FRICTION 0,85 son ~4,1 s — no 787.
 */
export function passTravelTime(distance: number, initialSpeed: number): number {
  const f = BALL.FRICTION_EFFECTIVE;
  const maxReach = initialSpeed / f;
  if (distance >= maxReach * 0.97) return -dlog(0.03) / f;
  return -dlog(1 - (distance * f) / initialSpeed) / f;
}

/**
 * Con cuanta fuerza hay que pegarle para que el balon LLEGUE.
 *
 * `arriveFraction` es que parte del alcance se quiere gastar: 0,95 llega
 * muriendose (comodo de controlar, bien para el pase corto al pie) y 0,8 llega
 * todavia con ritmo (lo que pide un pase largo).
 *
 * Existe porque la potencia se venia fijando como `distancia / 680`, una regla
 * calibrada para un balon que alcanza ~1100 unidades. Con la escala de modo el
 * alcance real en 11v11 es 608, asi que un pase de 500 salia con potencia 0,74
 * y se arrastraba 2,9 s — la IA planeaba pases que ella misma podia ver que no
 * llegaban. Lo tapaba un fallo de unidades en `passTravelTime`; al arreglarlo,
 * esto quedo a la vista.
 */
export function passPowerForDistance(distance: number, arriveFraction: number): number {
  const full = estimatePassSpeed("pass", 1);
  const needed = (distance * BALL.FRICTION_EFFECTIVE) / arriveFraction;
  // `estimatePassSpeed` va con la raiz de la potencia: se invierte al cuadrado.
  const ratio = needed / full;
  return clamp(ratio * ratio, 0, 1);
}

/** Velocidad inicial estimada del balon segun tipo y carga. */
export function estimatePassSpeed(kind: "pass" | "cross", power: number): number {
  const base = kind === "cross" ? KICK.CROSS_SPEED : KICK.PASS_SPEED;
  // La IA planifica con la velocidad base; el bonus de centro a tope se aplica
  // al ejecutar el golpeo (resolveKickPower), no aqui, para no sesgar el tiro.
  return modeKick(base) * Math.sqrt(clamp(power, 0.2, 1));
}

/**
 * Riesgo de intercepcion (0 = seguro, 1 = cortado seguro).
 * Proyecta si algun defensor puede alcanzar el vector antes que el balon.
 */
export function passInterceptRisk(
  from: Vec2,
  to: Vec2,
  passSpeed: number,
  opponents: readonly Player[],
): number {
  const travelDist = dist(from, to);
  const travelTime = passTravelTime(travelDist, passSpeed);
  let worst = 0;

  for (const rival of opponents) {
    if (rival.role === "gk") continue;

    const skills = roleSkills(rival);
    const maxSpeed = modeSpeed(PLAYER.MAX_SPEED) * rival.speedMultiplier * skills.anticipation;
    const intercept = closestPointOnSegment(rival.pos, from, to);
    const runDist = Math.max(0, dist(rival.pos, intercept) - rival.radius - 8);
    if (runDist <= 14) {
      worst = Math.max(worst, clamp(0.62 + (14 - runDist) / 28, 0.62, 1));
      continue;
    }

    const reaction = 0;
    const timeToReach = runDist / Math.max(maxSpeed, 80) + reaction;

    if (timeToReach >= travelTime) continue;

    const margin = (travelTime - timeToReach) / Math.max(travelTime, 0.05);
    worst = Math.max(worst, clamp(1 - margin * 1.2, 0, 1));
  }

  return worst;
}

/** Seguridad combinada: distancia al carril + intercepcion dinamica. */
export function passSafety(
  from: Vec2,
  to: Vec2,
  passSpeed: number,
  opponents: readonly Player[],
): number {
  let laneClear = Infinity;
  for (const rival of opponents) {
    laneClear = Math.min(laneClear, distanceToSegment(rival.pos, from, to));
  }
  const laneNorm = clamp(laneClear / AI.LANE_CLEARANCE, 0, 1.4);
  const intercept = passInterceptRisk(from, to, passSpeed, opponents);
  return laneNorm * (1 - intercept * 0.92);
}

export interface PassOption {
  target: Vec2;
  score: number;
  power: number;
  through: boolean;
  interceptRisk: number;
}

/** Pase directo al pie: receptor estatico o con poco lead. */
export function scoreDirectPass(
  passer: Player,
  _mate: Player,
  ball: Ball,
  target: Vec2,
  opponents: readonly Player[],
  advance: number,
  openness: number,
  power: number,
): PassOption {
  const skills = roleSkills(passer);
  const speed = estimatePassSpeed("pass", power);
  const safety = passSafety(ball.pos, target, speed, opponents);
  const interceptRisk = passInterceptRisk(ball.pos, target, speed, opponents);

  let score =
    (Math.max(advance, 20) / 120) *
    safety *
    skills.passing *
    skills.vision *
    100;

  if (openness >= AI.FREE_RADIUS) score *= 1.15;
  if (interceptRisk > 0.75) score *= 0.25;

  return { target, score, power, through: false, interceptRisk };
}

/**
 * Cuanto tarda alguien en llegar a un punto, contando por donde iba.
 *
 * Es la mitad del futbol que faltaba. Quien ya va lanzado hacia el balon se
 * ahorra el arranque; quien va en sentido contrario tiene que frenar y girar, y
 * eso cuesta tiempo. Sin este segundo termino el defensa cambiaba de sentido
 * gratis y ningun pase al hueco se ganaba jamas — que es justo lo que medimos:
 * 402 de 469 intentos descartados por perder la carrera.
 */
function timeToReach(
  from: Vec2,
  vel: Vec2,
  radius: number,
  target: Vec2,
  speed: number,
): number {
  const dx = target.x - from.x;
  const dy = target.y - from.y;
  const gap = len2(dx, dy) || 1;
  // Componente de la velocidad actual hacia el destino: + a favor, - en contra.
  const aligned = (vel.x * dx + vel.y * dy) / gap;
  const run = Math.max(0, gap - radius - Math.max(0, aligned) * AI.THROUGH_MOMENTUM);
  const turn = aligned < 0 ? AI.THROUGH_TURN_TIME * clamp(-aligned / Math.max(speed, 80), 0, 1) : 0;
  return run / Math.max(speed, 80) + turn;
}

/**
 * ¿Llega el que corre antes que nadie?
 *
 * Es la pregunta que faltaba, y la que separa un pase al hueco de un regalo.
 * `passInterceptRisk` mira si alguien corta el CARRIL; esto mira quien llega al
 * DESTINO, que es otra cosa: un balon puede cruzar limpio y acabar igualmente en
 * las manos del portero o en la bota del central que estaba mas cerca.
 *
 * El portero cuenta aqui, y NO en el riesgo de carril: dentro del area es el
 * primero que sale a comerse un pase largo, y no contarlo era justo lo que hacia
 * que el unico pase al hueco medido en 120 s acabara perdido.
 *
 * Devuelve el margen en segundos: >0 lo gana el nuestro, <0 lo gana el rival.
 */
export function receiverRace(
  mate: Player,
  target: Vec2,
  from: Vec2,
  passSpeed: number,
  opponents: readonly Player[],
): number {
  const ballTime = passTravelTime(dist(from, target), passSpeed);
  const mateSpeed = modeSpeed(PLAYER.MAX_SPEED) * mate.speedMultiplier;
  const mateTime = timeToReach(mate.pos, mate.vel, mate.radius, target, mateSpeed);

  // El receptor no puede llegar ANTES que el balon y quedarse esperando: el que
  // manda es el mas lento de los dos.
  const ours = Math.max(mateTime, ballTime);

  let theirs = Infinity;
  for (const rival of opponents) {
    const skills = roleSkills(rival);
    // El portero sale con su propia velocidad y sin descuento de anticipacion:
    // no tiene que controlar el balon, lo atrapa.
    const speed =
      modeSpeed(PLAYER.MAX_SPEED) *
      rival.speedMultiplier *
      (rival.role === "gk" ? 1 : skills.anticipation);
    const reaction = rival.role === "gk" ? 0 : AI.THROUGH_RIVAL_REACTION;
    theirs = Math.min(theirs, timeToReach(rival.pos, rival.vel, rival.radius, target, speed) + reaction);
  }

  return theirs - ours;
}

/**
 * El carril del pase al hueco: hacia donde, y hasta donde como mucho.
 *
 * Antes el destino era `mate.pos.x + dir * profundidad`: siempre recto y
 * siempre lo mismo, sin mirar donde estaba el hueco ni quien lo cubria. Eso no
 * es un pase al hueco, es un pase largo en la direccion en que ya miraba el
 * receptor.
 *
 * Se devuelven los dos extremos del carril para que el que pasa pueda elegir
 * PROFUNDIDAD, que es lo que de verdad decide un futbolista al levantar la
 * cabeza: se pone tan lejos como el companero pueda llegar, no tan lejos como
 * se pueda pegar.
 *
 *  - `deep`: el balon pasado de la linea del penultimo defensa (que es lo que
 *    hace que el pase ROMPA algo), sin meterse en el radio del portero.
 *  - `near`: lo minimo para que siga siendo un hueco y no un pase al pie.
 *  - la `y` se aparta de la cobertura mas cercana POR DELANTE del receptor; el
 *    que viene por detras no cubre nada, persigue.
 */
export function throughBallLane(
  mate: Player,
  opponents: readonly Player[],
  attackDir: number,
  ballX: number,
): { deepX: number; nearX: number; y: number } {
  const mateSkills = roleSkills(mate);
  const depth = AI.THROUGH_DEPTH * (0.75 + mateSkills.pace * 0.35);

  const line = offsideLineX(mate.side, ballX, opponents);
  const beyond = line + attackDir * AI.THROUGH_BEHIND_LINE;
  const straight = mate.pos.x + attackDir * depth;
  let deepX = attackDir > 0 ? Math.max(straight, beyond) : Math.min(straight, beyond);

  // Que no acabe en las manos del portero.
  const goalX = mate.side === "home" ? FIELD.WIDTH : 0;
  const keeperGuard = goalX - attackDir * AI.THROUGH_GOAL_MARGIN;
  deepX = attackDir > 0 ? Math.min(deepX, keeperGuard) : Math.max(deepX, keeperGuard);
  deepX = clamp(deepX, mate.radius, FIELD.WIDTH - mate.radius);

  const nearX = clamp(
    mate.pos.x + attackDir * AI.THROUGH_MIN_GAIN,
    mate.radius,
    FIELD.WIDTH - mate.radius,
  );

  let y = mate.pos.y + mate.vel.y * 0.22;
  let cover: Player | null = null;
  let coverDist = Infinity;
  for (const rival of opponents) {
    if (rival.role === "gk") continue;
    if ((rival.pos.x - mate.pos.x) * attackDir <= 0) continue;
    const d = dist(rival.pos, mate.pos);
    if (d < coverDist) {
      coverDist = d;
      cover = rival;
    }
  }
  if (cover && coverDist < AI.THROUGH_COVER_RADIUS) {
    const away = Math.sign(mate.pos.y - cover.pos.y) || 1;
    y += away * AI.THROUGH_COVER_SHIFT * (1 - coverDist / AI.THROUGH_COVER_RADIUS);
  }
  y = clamp(y, mate.radius, FIELD.HEIGHT - mate.radius);

  return { deepX, nearX, y };
}

/** Un pase al hueco ya resuelto: donde va y si se puede ganar. */
export interface ThroughBall {
  /** Donde cae el balon, NO donde esta el receptor. */
  target: Vec2;
  power: number;
  /** Cuanto adelanta el balon respecto a donde esta el receptor ahora. */
  gain: number;
  /**
   * Calidad FISICA, 0-1,6. **No es una puntuacion comparable con nada.**
   *
   * Es el factor que `evaluatePass` multiplica dentro de SU formula, igual que
   * `freeMul` para el pase al pie. Devolver aqui una "puntuacion" propia fue el
   * error que mantuvo el pase al hueco en el 0% de los pases: se comparaba
   * `through.score > score * 0,82` entre dos monedas distintas — el hueco vivia
   * en p50=18-35 y el pase al pie en p50=492-1855, un producto de siete
   * factores sin techo. La ganaba el pie 25-50x PASARA LO QUE PASARA. Es el
   * mismo fallo que ya se habia arreglado entre tiro y pase con
   * `normalizeActionScore`, repetido un nivel mas abajo.
   */
  quality: number;
  /** Segundos de ventaja del receptor sobre el primer rival. */
  race: number;
  interceptRisk: number;
}

/**
 * Pase al hueco: vision y pase del lanzador contra anticipacion y colocacion de
 * los defensas, y sobre todo la CARRERA por el balon, que es la que manda.
 *
 * Se prueban varias profundidades de mas a menos y se coge la primera que el
 * companero gana. Es lo que hace que el mismo pase sea distinto contra una
 * defensa adelantada que contra una replegada, sin ninguna constante nueva que
 * calibrar: el hueco lo define la defensa, no un numero.
 */
export function scoreThroughPass(
  passer: Player,
  mate: Player,
  ball: Ball,
  opponents: readonly Player[],
  attackDir: number,
  ballX: number = ball.pos.x,
): ThroughBall | null {
  const skills = roleSkills(passer);

  // Se juzga donde esta el receptor AL SALIR el pase, que es lo que dice la
  // regla: correr despues hacia el balon es legal, y es justo la jugada.
  if (isInOffsidePosition(mate.pos.x, passer.side, ballX, opponents, 0, [mate])) {
    return null;
  }

  const lane = throughBallLane(mate, opponents, attackDir, ballX);

  /*
   * El carril se recorta a lo que el balon ALCANZA, antes de probar nada.
   *
   * Sin esto, dos de los tres tanteos de profundidad caian siempre fuera de
   * alcance y el pase se descartaba entero: 1096 de 1268 intentos morian ahi.
   * El hueco existia y el companero podia ganarlo — lo que no llegaba era el
   * balon. Recortar el carril primero hace que los tanteos exploren el tramo
   * que de verdad se puede jugar.
   */
  const maxTravel = (estimatePassSpeed("pass", 1) / BALL.FRICTION_EFFECTIVE) * AI.THROUGH_MAX_REACH;
  const sideways = Math.abs(lane.y - ball.pos.y);
  if (sideways >= maxTravel) return null;
  const alongX = Math.sqrt(maxTravel * maxTravel - sideways * sideways);
  const reachX = ball.pos.x + attackDir * alongX;
  const deepX =
    attackDir > 0 ? Math.min(lane.deepX, reachX) : Math.max(lane.deepX, reachX);

  const span = deepX - lane.nearX;
  // O el hueco va al reves que el ataque, o ni el minimo esta a tiro.
  if (span * attackDir <= 0) return null;

  for (const step of AI.THROUGH_DEPTH_STEPS) {
    const target = { x: lane.nearX + span * step, y: lane.y };
    const gain = (target.x - mate.pos.x) * attackDir;
    if (gain < AI.THROUGH_MIN_GAIN) { continue; }

    /*
     * ¿Llega siquiera el balon, y con que fuerza hay que pegarle?
     *
     * Es el limite que lo decidia todo y no lo miraba nadie. En 11v11 un pase
     * raso a tope alcanza 608 unidades en un campo de 1600, y cerca de ese tope
     * el balon se arrastra: 500 u tardan 2,0 s y 700 no llegan nunca. La IA
     * proponia huecos a 600-700 u —3,4 s de vuelo de media— para los que
     * cualquier defensa llegaba andando. No era un problema de valoracion: no
     * habia pase que dar.
     *
     * Asi que la potencia no se fija, se DESPEJA: cuanta hace falta para que el
     * balon llegue todavia vivo. Si no la hay ni a tope, no existe ese pase.
     * (Un `power` proporcional a la distancia se muerde la cola — a mas lejos,
     * mas potencia, pero el umbral de alcance sube con ella y nunca se alcanza.)
     */
    const travel = dist(ball.pos, target);
    const full = estimatePassSpeed("pass", 1);
    const needed = (travel * BALL.FRICTION_EFFECTIVE) / AI.THROUGH_MAX_REACH;
    if (needed > full) { continue; }
    // `estimatePassSpeed` va con la raiz de la potencia: se invierte al cuadrado.
    const ratio = needed / full;
    const power = clamp(ratio * ratio, AI.THROUGH_MIN_POWER, 1);
    const speed = estimatePassSpeed("pass", power);

    const interceptRisk = passInterceptRisk(ball.pos, target, speed, opponents);
    if (interceptRisk > 0.92 && skills.vision * skills.passing < 1.15) { continue; }

    const race = receiverRace(mate, target, ball.pos, speed, opponents);
    if (race < AI.THROUGH_MIN_RACE) { continue; }

    let bestDef = 0.85;
    for (const rival of opponents) {
      if (rival.role === "gk") continue;
      const def = roleSkills(rival);
      bestDef = Math.max(bestDef, def.anticipation * def.placement);
    }

    const attackEdge = clamp((skills.vision * skills.passing) / bestDef, 0.5, 1.45);
    // Llegar por los pelos vale menos que llegar solo.
    const raceMul = clamp(0.45 + race / AI.THROUGH_RACE_COMFORT, 0.45, 1.25);

    /*
     * El riesgo de intercepcion NO entra aqui, y es a proposito.
     *
     * `evaluatePass` ya multiplica por la holgura del carril hacia este mismo
     * destino, igual que hace con el pase al pie. Meterlo tambien en `quality`
     * cobraba al hueco dos veces por el mismo peligro mientras al pie se le
     * cobraba una — medido: el hueco salia 5x por debajo. Arriba sigue siendo
     * un veto duro, que es donde tiene sentido.
     */
    let quality = attackEdge * raceMul;
    if (mate.tacticRole === "st" || mate.tacticRole === "wm") quality *= 1.12;
    return { target, power, gain, quality: clamp(quality, 0, 1.6), race, interceptRisk };
  }

  return null;
}

/** Error angular de pase/remate segun distancia, presion y habilidad. */
export function passAngularError(
  player: Player,
  passDist: number,
  pressured: boolean,
  kind: "pass" | "cross" | "shoot",
): number {
  const skills = roleSkills(player);
  if (kind === "shoot") {
    let err = KICK.SHOT_ERROR_BASE * (1 + passDist / KICK.SHOT_ERROR_DIST_SCALE);
    if (pressured) err *= AI.PASS_ERROR_PRESSURE;
    // Colocacion del rol ≈ remate: un ST coloca mejor que un CB.
    const placed = err / skills.placement;
    const withTalent = player.talent ? placed / player.talent.shooting : placed;
    // Carrera: la IA del barrio chuta peor (ver `skill.ts`).
    return player.aiSkill === undefined ? withTalent : withTalent * aimScale(player);
  }
  // Escala multiplicativa: a distancia de referencia el error ~duplica la base.
  let err = AI.PASS_ERROR_BASE * (1 + passDist / AI.PASS_ERROR_DIST_SCALE);
  if (pressured) err *= AI.PASS_ERROR_PRESSURE;
  if (kind === "cross") err *= 1.18;
  // En modos muy abiertos, un poco mas de imprecision mantiene la circulacion.
  if (fieldPlayersPerTeam() <= 3) err *= 1.25;
  const passed = err / skills.passing;
  const withTalent = player.talent ? passed / player.talent.passing : passed;
  return player.aiSkill === undefined ? withTalent : withTalent * aimScale(player);
}
